/* ============================================================================
 * hub-screenings.js — "at Harry's": the homepage bar + the schedule on
 * /pages/harrys. Data: the sport-events Edge Function (public GET), which is
 * refreshed from Harry's own calendar every 3 hours, server-side.
 * Design: docs/specs/2026-10-06-sport-events-design.md (Part A).
 * ----------------------------------------------------------------------------
 * ES5 ON PURPOSE (no ?., ??, arrow functions, let/const): loads on the
 * homepage, and _verify/es5-storefront-test.mjs exists because one modern
 * token silently kills a whole file on older Android phones.
 *
 * ALL TIME MATHS IS SINGAPORE TIME, WHATEVER THE VISITOR'S CLOCK SAYS.
 * Singapore is a fixed UTC+8 (no DST since 1982), so every date/time here is
 * computed by shifting the epoch +8h and reading UTC fields — never getHours()
 * or toLocale*(), which would follow the visitor's own timezone.
 *
 * NEVER SHOW SOMETHING WE CANNOT VOUCH FOR. No data, a failed fetch, or data
 * whose last successful refresh is older than 72 hours => the bar stays hidden
 * and the list shows a plain "see Harry's site" line instead.
 *
 * The pure helpers are exposed as window.HubScreenings for
 * _verify/screening-state-test.mjs, which runs them at fixed clock times.
 * ========================================================================== */
(function () {
  'use strict';

  var SGT_MS = 8 * 3600 * 1000;
  var DAY_MS = 24 * 3600 * 1000;
  var STALE_MS = 72 * 3600 * 1000;
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var SPORT_LABEL = {
    'football': 'Football', 'rugby': 'Rugby', 'afl': 'AFL', 'cricket': 'Cricket', 'f1': 'F1',
    'tennis': 'Tennis', 'american-football': 'American football', 'ufc': 'UFC', 'boxing': 'Boxing',
    'horse-racing': 'Racing'
  };

  /* ── pure helpers ─────────────────────────────────────────────────────── */

  function ms(iso) { var t = Date.parse(iso); return isNaN(t) ? NaN : t; }

  /** Singapore calendar day as a sortable key, e.g. "2026-10-09". */
  function sgtDayKey(t) { return new Date(t + SGT_MS).toISOString().slice(0, 10); }

  /** "7:30pm", "10pm", "12am" — Singapore wall clock. */
  function sgtTime(t) {
    var d = new Date(t + SGT_MS);
    var h = d.getUTCHours(), m = d.getUTCMinutes();
    var ap = h < 12 ? 'am' : 'pm';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + (m ? ':' + (m < 10 ? '0' : '') + m : '') + ap;
  }

  /** "Fri 9 Oct" — Singapore calendar. */
  function sgtDate(t) {
    var d = new Date(t + SGT_MS);
    return DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()];
  }

  /** "Today" / "Tomorrow" / "Fri 9 Oct" relative to `now`, in Singapore days. */
  function dayLabel(t, now) {
    var k = sgtDayKey(t);
    if (k === sgtDayKey(now)) return 'Today';
    if (k === sgtDayKey(now + DAY_MS)) return 'Tomorrow';
    return sgtDate(t);
  }

  /** A match ("A v B") reads on its own; anything else (SINGAPORE GP) needs its second line. */
  function shortName(ev) {
    var t = ev.title || '';
    if (/\sv\s/i.test(t) || !ev.subtitle) return t;
    return t + ' · ' + ev.subtitle;
  }

  /** Normalise + drop anything unusable or already over. Sorted by start. */
  function upcoming(events, now) {
    var out = [];
    for (var i = 0; i < (events || []).length; i++) {
      var e = events[i];
      var s = ms(e && e.starts_at), f = ms(e && e.ends_at);
      if (!e || !e.title || isNaN(s) || isNaN(f) || f <= s) continue;
      if (f <= now) continue;
      out.push({ title: e.title, subtitle: e.subtitle || null, sport: e.sport || null,
                 start: s, end: f, outlets: e.outlets || [] });
    }
    out.sort(function (a, b) { return a.start - b.start || a.end - b.end; });
    return out;
  }

  /**
   * What the bar should say at `now`.
   *   { kind: 'live',  items: [...live], laterToday: n }
   *   { kind: 'today', items: [...later today] }
   *   { kind: 'next',  items: [the next one] }
   *   { kind: 'none' } — no data, stale, or nothing ahead: render NOTHING.
   */
  function barState(data, now) {
    if (!data || !data.ok || !data.fetched_at) return { kind: 'none', why: 'no data' };
    var fetched = ms(data.fetched_at);
    if (isNaN(fetched) || now - fetched > STALE_MS) return { kind: 'none', why: 'stale' };
    var ev = upcoming(data.events, now);
    var live = [], today = [], i;
    var todayKey = sgtDayKey(now);
    for (i = 0; i < ev.length; i++) {
      if (ev[i].start <= now) live.push(ev[i]);
      else if (sgtDayKey(ev[i].start) === todayKey) today.push(ev[i]);
    }
    if (live.length) return { kind: 'live', items: live, laterToday: today.length };
    if (today.length) return { kind: 'today', items: today };
    if (ev.length) return { kind: 'next', items: [ev[0]] };
    return { kind: 'none', why: 'nothing ahead' };
  }

  /** The bar as plain text parts: { lead, body } — what the DOM renders and the aria-label reads. */
  function barText(state, now, maxItems) {
    var n = maxItems || 2, parts = [], i;
    if (state.kind === 'live') {
      for (i = 0; i < state.items.length && i < n; i++) {
        parts.push(shortName(state.items[i]) + ' · until ' + sgtTime(state.items[i].end));
      }
      var more = state.items.length - Math.min(state.items.length, n) + state.laterToday;
      return { lead: "Live now at Harry's", body: parts.join(' · ') + (more > 0 ? ' · +' + more : '') };
    }
    if (state.kind === 'today') {
      for (i = 0; i < state.items.length && i < n; i++) {
        parts.push(sgtTime(state.items[i].start) + ' ' + shortName(state.items[i]));
      }
      var rest = state.items.length - Math.min(state.items.length, n);
      return { lead: "Today at Harry's", body: parts.join(' · ') + (rest > 0 ? ' · +' + rest : '') };
    }
    if (state.kind === 'next') {
      var e = state.items[0];
      var lbl = dayLabel(e.start, now);
      // Time first (as in "Today"), so a narrow screen never truncates the when.
      return { lead: (lbl === 'Tomorrow' ? "Tomorrow at Harry's" : "Next at Harry's"),
               body: (lbl === 'Tomorrow' ? '' : lbl + ' ') + sgtTime(e.start) + ' · ' + shortName(e) };
    }
    return null;
  }

  /** The schedule, grouped by Singapore day: [{ key, label, items }]. Live rows flagged. */
  function schedule(data, now) {
    if (!data || !data.ok || !data.fetched_at) return null;
    var fetched = ms(data.fetched_at);
    if (isNaN(fetched) || now - fetched > STALE_MS) return null;
    var ev = upcoming(data.events, now), days = [], byKey = {};
    for (var i = 0; i < ev.length; i++) {
      var e = ev[i];
      e.live = e.start <= now;
      // A live game that started yesterday evening belongs under Today.
      var k = e.live ? sgtDayKey(now) : sgtDayKey(e.start);
      if (!byKey[k]) { byKey[k] = { key: k, label: e.live ? 'Today' : dayLabel(e.start, now), items: [] }; days.push(byKey[k]); }
      byKey[k].items.push(e);
    }
    return days;
  }

  /**
   * Everything the homepage bar shows, decided in one pure place.
   *   Harry's first (live / today / next). "+N more today in SG" alongside when the server counts other Singapore
   *   sport ON today (data.others_today — a number the server computes with the What's On page's own rules).
   *   Nothing at Harry's but other sport today -> "Today in Singapore · N events", linking to the calendar.
   *   Nothing at all, or no data -> null (the bar hides).
   */
  function barView(data, now, maxItems) {
    var n = data && typeof data.others_today === 'number' && data.others_today > 0 ? data.others_today : 0;
    // Phones (one item) get the short form: the long one collided with the label on a 375px screen.
    var more = n ? '+' + n + (maxItems === 1 ? ' today in SG' : ' more today in SG') : null;
    var st = barState(data, now);
    var tx = barText(st, now, maxItems);
    if (tx) return { kind: st.kind, lead: tx.lead, body: tx.body, main: 'harrys', more: more };
    if (n) return { kind: 'sg-today', lead: 'Today in Singapore', body: n + (n === 1 ? ' event' : ' events') + ' · fixtures, races & more', main: 'calendar', more: null };
    return null;
  }

  var api = { barView: barView, sgtTime: sgtTime, sgtDate: sgtDate, sgtDayKey: sgtDayKey, dayLabel: dayLabel,
              upcoming: upcoming, barState: barState, barText: barText, schedule: schedule,
              shortName: shortName, SPORT_LABEL: SPORT_LABEL };
  window.HubScreenings = api;

  /* ── DOM ──────────────────────────────────────────────────────────────── */

  if (typeof document === 'undefined') return;

  var cache = {};
  function load(endpoint) {
    if (!cache[endpoint]) {
      cache[endpoint] = fetch(endpoint, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        ['catch'](function () { return null; });
    }
    return cache[endpoint];
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderBar(el) {
    var endpoint = el.getAttribute('data-endpoint');
    if (!endpoint) { el.hidden = true; return; }
    var main = el.querySelector('[data-sbar-main]'), moreEl = el.querySelector('[data-sbar-more]');
    var calendar = el.getAttribute('data-calendar-url') || '/pages/whats-on';
    load(endpoint).then(function (data) {
      var now = Date.now();
      var mobile = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
      var v = barView(data, now, mobile ? 1 : 2);
      if (!v) { el.hidden = true; el.setAttribute('data-state', 'none'); return; }
      el.setAttribute('data-state', v.kind);
      var lead = el.querySelector('[data-sbar-lead]'), body = el.querySelector('[data-sbar-body]');
      if (lead) lead.textContent = v.lead;
      if (body) body.textContent = v.body;
      if (main) {
        if (v.main === 'calendar') main.setAttribute('href', calendar + '?when=today');
        main.setAttribute('aria-label', v.lead + (v.body ? ': ' + v.body : '') + (v.main === 'calendar' ? ". See what's on." : '. See all screenings.'));
      }
      if (moreEl) {
        if (v.more) { moreEl.textContent = v.more + ' \u2192'; moreEl.setAttribute('aria-label', v.more + ". See what's on today."); moreEl.hidden = false; }
        else moreEl.hidden = true;
      }
      el.hidden = false;
    });
  }

  function renderList(el) {
    var endpoint = el.getAttribute('data-endpoint');
    var target = el.querySelector('[data-screenings-days]');
    var fallback = el.querySelector('[data-screenings-fallback]');
    if (!endpoint || !target) return;
    load(endpoint).then(function (data) {
      var now = Date.now();
      var days = schedule(data, now);
      if (!days || !days.length) {
        target.innerHTML = '';
        if (fallback) fallback.hidden = false;
        el.setAttribute('data-state', 'none');
        return;
      }
      var html = '';
      for (var d = 0; d < days.length; d++) {
        var day = days[d];
        html += '<li class="scr__day"><h3 class="scr__dayh">' + esc(day.label) + '</h3><ol class="scr__rows" role="list">';
        for (var i = 0; i < day.items.length; i++) {
          var e = day.items[i];
          var o = e.outlets || [];
          var outletsHtml = '';
          if (o.length > 3) {
            outletsHtml = '<details class="scr__outlets"><summary>' + esc(o.slice(0, 3).join(', ')) +
              ' <span class="scr__more">+' + (o.length - 3) + '</span></summary>' + esc(o.slice(3).join(', ')) + '</details>';
          } else if (o.length) {
            outletsHtml = '<p class="scr__outlets">' + esc(o.join(', ')) + '</p>';
          }
          html += '<li class="scr__row' + (e.live ? ' is-live' : '') + '">' +
            '<p class="scr__time"><time datetime="' + new Date(e.start).toISOString() + '">' + esc(sgtTime(e.start)) +
            '</time><span class="scr__dash"> – </span><time datetime="' + new Date(e.end).toISOString() + '">' + esc(sgtTime(e.end)) + '</time>' +
            (e.live ? '<span class="scr__live">Live</span>' : '') + '</p>' +
            '<div class="scr__what"><p class="scr__title">' + esc(e.title) + '</p>' +
            (e.subtitle ? '<p class="scr__sub">' + esc(e.subtitle) + '</p>' : '') +
            outletsHtml + '</div>' +
            (e.sport && SPORT_LABEL[e.sport] ? '<span class="scr__chip">' + esc(SPORT_LABEL[e.sport]) + '</span>' : '') +
            '</li>';
        }
        html += '</ol></li>';
      }
      target.innerHTML = html;
      if (fallback) fallback.hidden = true;
      el.setAttribute('data-state', 'ok');
    });
  }

  function init() {
    var bars = document.querySelectorAll('[data-screening-bar]');
    for (var i = 0; i < bars.length; i++) renderBar(bars[i]);
    var lists = document.querySelectorAll('[data-screenings]');
    for (var j = 0; j < lists.length; j++) renderList(lists[j]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
